/**
 * Push Subscription Service
 * AIDLC Administrative Backend
 *
 * Subscribes this browser to push notifications once the user is signed in (PUSH-SUB-1, BL-7eaa1b29).
 * Push is optional: when the server has no VAPID keys (503), the browser has no push support, or the user
 * has blocked notifications, nothing is subscribed and nothing is shown; the reason goes to the debug log.
 * A real fault (the subscribe or register call failing) is a console warning; the portal keeps working.
 */

import apiClient from './api';
import { isPushNotConfigured, vapidKeyToBytes, vapidPublicKeyFrom } from '../utils/webPushKey';

const LOG_PREFIX = '[Push]';

/** Why no subscription was made, or that one was. Returned for tests and the debug log. */
export type PushSubscribeOutcome =
  | 'unsupported'
  | 'blocked'
  | 'already-subscribed'
  | 'not-configured'
  | 'declined'
  | 'subscribed'
  | 'failed';

/** The server's VAPID public key, or null when push is not set up there (a 503, said only in the debug log). */
async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const { data } = await apiClient.get<{ publicKey?: string }>('/notifications/vapid-key');
    return vapidPublicKeyFrom(data);
  } catch (error) {
    if (isPushNotConfigured(error)) return null;
    throw error;
  }
}

function isPermissionDenied(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === 'NotAllowedError';
}

/** Subscribes this browser and registers the subscription with the server; never throws. */
export async function subscribeToPush(): Promise<PushSubscribeOutcome> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    console.debug(`${LOG_PREFIX} This browser has no push support; not subscribing.`);
    return 'unsupported';
  }
  if (Notification.permission === 'denied') {
    console.debug(`${LOG_PREFIX} Notifications are blocked for this site; not subscribing.`);
    return 'blocked';
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    if (await registration.pushManager.getSubscription()) return 'already-subscribed';

    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) {
      console.debug(`${LOG_PREFIX} Push is not set up on the server; not subscribing.`);
      return 'not-configured';
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyToBytes(publicKey),
    });
    await apiClient.post('/notifications/subscribe', { subscription: subscription.toJSON() });
    console.info(`${LOG_PREFIX} Subscription registered`);
    return 'subscribed';
  } catch (error) {
    if (isPermissionDenied(error)) {
      console.debug(`${LOG_PREFIX} Notifications were not allowed; not subscribing.`);
      return 'declined';
    }
    console.warn(`${LOG_PREFIX} Subscription failed:`, error);
    return 'failed';
  }
}
